import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Activity,
  Zap,
  BookOpen,
  TrendingUp,
  Sparkles,
  Crown,
  Star,
  Gem,
  UserPlus,
  Coins,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Rocket,
  FileText,
} from 'lucide-react';
import type {
  DashboardStats,
  DashboardLeaderEntry,
  ActivityDay,
} from '@/lib/dashboard-service';
import { formatDistanceToNow } from 'date-fns';
import ChefCoinIcon from '@/components/ui/ChefCoinIcon';

interface DashboardOverviewProps {
  stats: DashboardStats;
  isLoading?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting helpers
// ─────────────────────────────────────────────────────────────────────────────
const formatNumber = (n: number): string => (Number.isFinite(n) ? n.toLocaleString() : '0');
const formatCompact = (n: number): string => {
  if (!Number.isFinite(n)) return '0';
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
};
const formatPercent = (num: number, denom: number): string => {
  if (denom <= 0) return '0%';
  return `${Math.round((num / denom) * 100)}%`;
};

const initialsFor = (entry: DashboardLeaderEntry): string => {
  const base = (entry.name && entry.name.trim()) || entry.email || '';
  const parts = base.split(/[\s@._-]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const getSubscriptionBadge = (subscription: string) => {
  switch (subscription) {
    case 'premium':
      return <Badge className="bg-purple-500 text-white text-[10px]"><Gem className="h-2.5 w-2.5 mr-0.5" />Premium</Badge>;
    case 'standard':
      return <Badge variant="secondary" className="text-[10px]"><Star className="h-2.5 w-2.5 mr-0.5" />Standard</Badge>;
    case 'free':
      return <Badge variant="outline" className="text-[10px]">Free</Badge>;
    case 'banned':
      return <Badge variant="destructive" className="text-[10px]">Banned</Badge>;
    default:
      return <Badge variant="secondary" className="text-[10px]">{subscription}</Badge>;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-28 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <div className="h-56 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse lg:col-span-2" />
      <div className="h-56 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-32 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="h-72 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse" />
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Reusable pieces
// ─────────────────────────────────────────────────────────────────────────────
type KpiCardProps = {
  label: string;
  value: string | number;
  icon: React.ElementType;
  hint?: string;
  trend?: { value: string; direction: 'up' | 'down' | 'neutral' };
  accent: 'orange' | 'emerald' | 'sky' | 'violet';
};

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon: Icon, hint, trend, accent }) => {
  const accentMap = {
    orange:   { ring: 'from-orange-400/15 to-transparent', dot: 'bg-orange-500', icon: 'text-orange-500' },
    emerald:  { ring: 'from-emerald-400/15 to-transparent', dot: 'bg-emerald-500', icon: 'text-emerald-500' },
    sky:      { ring: 'from-sky-400/15 to-transparent', dot: 'bg-sky-500', icon: 'text-sky-500' },
    violet:   { ring: 'from-violet-400/15 to-transparent', dot: 'bg-violet-500', icon: 'text-violet-500' },
  }[accent];

  return (
    <Card className="relative overflow-hidden rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm transition-shadow hover:shadow-md">
      {/* Soft accent wash */}
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${accentMap.ring}`} />
      <CardContent className="relative p-4 sm:p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
            <span className={`h-1.5 w-1.5 rounded-full ${accentMap.dot}`} />
            {label}
          </div>
          <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 dark:bg-gray-800/80 ${accentMap.icon}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
          {typeof value === 'number' ? formatNumber(value) : value}
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-xs">
          {hint && <span className="text-gray-500 dark:text-gray-400">{hint}</span>}
          {trend && (
            <span className={`inline-flex items-center gap-0.5 font-medium ${
              trend.direction === 'up' ? 'text-emerald-600 dark:text-emerald-400'
                : trend.direction === 'down' ? 'text-rose-600 dark:text-rose-400'
                : 'text-gray-500 dark:text-gray-400'
            }`}>
              {trend.direction === 'up' && <ArrowUpRight className="h-3 w-3" />}
              {trend.direction === 'down' && <ArrowDownRight className="h-3 w-3" />}
              {trend.value}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

type CompositionCardProps = {
  title: string;
  icon: React.ElementType;
  iconClass?: string;
  segments: { label: string; value: number; color: string; icon?: React.ElementType }[];
  total: number;
};

const CompositionCard: React.FC<CompositionCardProps> = ({ title, icon: Icon, iconClass, segments, total }) => {
  const safeTotal = total > 0 ? total : 1;
  return (
    <Card className="rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
          <Icon className={`h-4 w-4 ${iconClass ?? ''}`} />
          {title}
          <span className="ml-auto text-xs font-normal text-gray-400">{total}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Segmented bar */}
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
          {segments.map((s, i) => (
            s.value > 0 && (
              <div
                key={i}
                className={`${s.color} h-full`}
                style={{ width: `${(s.value / safeTotal) * 100}%` }}
                title={`${s.label}: ${s.value}`}
              />
            )
          ))}
        </div>
        {/* Legend */}
        <div className="space-y-1.5">
          {segments.map((s, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                <span className={`h-2 w-2 rounded-full ${s.color}`} />
                {s.icon && <s.icon className="h-3 w-3 text-gray-400" />}
                {s.label}
              </span>
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {formatNumber(s.value)}
                <span className="ml-1 text-[10px] text-gray-400">{formatPercent(s.value, total)}</span>
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Activity pulse — 14-day teal/orange bar chart + ChefCoin dual-line overlay
// ─────────────────────────────────────────────────────────────────────────────
const ActivityPulse: React.FC<{ days: ActivityDay[] }> = ({ days }) => {
  const data = useMemo(() => {
    if (!days || days.length === 0) return null;
    const maxRecipes = Math.max(1, ...days.map(d => d.recipesCreated));
    const maxCoin = Math.max(1, ...days.map(d => Math.max(d.coinsEarned, d.coinsSpent)));

    // Build SVG paths for the chefcoin flow lines (scaled to the chart area).
    const W = 320; // viewBox width (scales responsively)
    const H = 70;
    const padX = 4;
    const stepX = days.length > 1 ? (W - padX * 2) / (days.length - 1) : 0;
    const y = (v: number) => H - (v / maxCoin) * (H - 8) - 4;
    const pts = (key: 'coinsEarned' | 'coinsSpent') =>
      days.map((d, i) => `${padX + i * stepX},${y(d[key])}`).join(' ');

    const totalRecipes = days.reduce((s, d) => s + d.recipesCreated, 0);
    const totalEarned = days.reduce((s, d) => s + d.coinsEarned, 0);
    const totalSpent = days.reduce((s, d) => s + d.coinsSpent, 0);
    return { maxRecipes, maxCoin, pts, totalRecipes, totalEarned, totalSpent };
  }, [days]);

  if (!data) {
    return (
      <div className="flex h-44 items-center justify-center text-sm text-gray-400">
        Activity telemetry unavailable
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Legend + totals */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-sm bg-orange-500" /> Recipes
          <span className="font-semibold text-gray-700 dark:text-gray-200">{formatNumber(data.totalRecipes)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0 w-3 rounded-full border-t-2 border-emerald-500" /> Earned
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">{formatNumber(data.totalEarned)}</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0 w-3 rounded-full border-t-2 border-rose-500" /> Spent
          <span className="font-semibold text-rose-600 dark:text-rose-400">{formatNumber(data.totalSpent)}</span>
        </span>
      </div>

      {/* Recipe creations — bar chart */}
      <div className="flex items-end gap-0.5 sm:gap-1 h-24" aria-label="Recipes created per day">
        {days.map((d) => {
          const h = data.maxRecipes > 0 ? (d.recipesCreated / data.maxRecipes) * 100 : 0;
          return (
            <div key={d.date} className="group relative flex-1 h-full flex items-end justify-center" title={`${d.date}: ${d.recipesCreated} recipe(s)`}>
              <div
                className="w-full rounded-t-md bg-gradient-to-t from-orange-500/70 to-orange-400 transition-all hover:from-orange-600 hover:to-orange-500"
                style={{ height: `${Math.max(h, d.recipesCreated > 0 ? 4 : 0)}%`, minHeight: d.recipesCreated > 0 ? '3px' : '0' }}
              />
            </div>
          );
        })}
      </div>

      {/* ChefCoin flow — SVG dual-line chart */}
      <div className="relative">
        <svg viewBox="0 0 320 70" preserveAspectRatio="none" className="w-full h-16" aria-hidden="true">
          <polyline points={data.pts('coinsEarned')} fill="none" stroke="#10b981" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
          <polyline points={data.pts('coinsSpent')} fill="none" stroke="#f43f5e" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
        <div className="mt-1 flex justify-between text-[10px] text-gray-400">
          <span>{days[0]?.date.slice(5)}</span>
          <span>{days[Math.floor(days.length / 2)]?.date.slice(5)}</span>
          <span>{days[days.length - 1]?.date.slice(5)}</span>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Leaderboard card — used for power users / spenders / creators / recent signups
// ─────────────────────────────────────────────────────────────────────────────
const LeaderList: React.FC<{ entries: DashboardLeaderEntry[]; emptyText: string }> = ({ entries, emptyText }) => {
  if (entries.length === 0) {
    return <div className="py-6 text-center text-xs text-gray-400">{emptyText}</div>;
  }
  return (
    <div className="space-y-2">
      {entries.map((e, i) => (
        <div key={e.userId + i} className="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            i === 0 ? 'bg-amber-400 text-amber-900'
              : i === 1 ? 'bg-gray-300 text-gray-700'
              : i === 2 ? 'bg-orange-400/80 text-orange-900'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {i + 1}
          </div>
          <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-white text-[10px] flex items-center justify-center font-semibold uppercase">
            {initialsFor(e)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{e.name || e.email}</div>
            <div className="text-[10px] text-gray-400 truncate">{e.email}</div>
          </div>
          <div className="text-right">
            <div className="text-xs font-semibold text-gray-700 dark:text-gray-200">{e.metricLabel}</div>
            {e.timestamp && (
              <div className="text-[10px] text-gray-400">{formatDistanceToNow(e.timestamp, { addSuffix: true })}</div>
            )}
          </div>
          {getSubscriptionBadge(e.subscription)}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Top studio users leaderboard (different shape — rank by studio uses count)
// ─────────────────────────────────────────────────────────────────────────────
const StudioPowerList: React.FC<{ stats: DashboardStats }> = ({ stats }) => {
  const entries = stats.topStudioUsers;
  if (entries.length === 0) {
    return <div className="py-6 text-center text-xs text-gray-400">No studio usage yet</div>;
  }
  return (
    <div className="space-y-2">
      {entries.slice(0, 5).map((u, i) => (
        <div key={u.userId} className="flex items-center gap-3 rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
            i === 0 ? 'bg-amber-400 text-amber-900'
              : i === 1 ? 'bg-gray-300 text-gray-700'
              : i === 2 ? 'bg-orange-400/80 text-orange-900'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
          }`}>
            {i + 1}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{u.name || u.email}</div>
            <div className="flex items-center gap-2 text-[10px] text-gray-400">
              <span className="truncate">{u.email}</span>
              {u.lastStudioUse && <span>· {formatDistanceToNow(u.lastStudioUse, { addSuffix: true })}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5 text-orange-500" />
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">{formatNumber(u.studioUsesCount)}</span>
          </div>
          {getSubscriptionBadge(u.subscription)}
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const DashboardOverview: React.FC<DashboardOverviewProps> = ({ stats, isLoading = false }) => {
  if (isLoading || !stats) return <LoadingSkeleton />;

  const studioPerUser = stats.totalUsers > 0 ? stats.totalStudioUses / stats.totalUsers : 0;
  const activeRateLabel = stats.totalUsers > 0 ? formatPercent(stats.activeUsersToday, stats.totalUsers) : '0%';

  return (
    <div className="space-y-6">
      {/* ── Hero KPI strip ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Total Users"
          value={stats.totalUsers}
          icon={Users}
          accent="orange"
          hint={`${stats.newUsersThisWeek} new this week`}
          trend={{
            value: `${stats.newUsersToday} today`,
            direction: stats.newUsersThisWeek > 0 ? 'up' : 'neutral',
          }}
        />
        <KpiCard
          label="Active 24h"
          value={stats.activeUsersToday}
          icon={Activity}
          accent="emerald"
          hint={`${formatNumber(stats.activeUsersThisWeek)} this week`}
          trend={{ value: activeRateLabel, direction: 'up' }}
        />
        <KpiCard
          label="Studio Uses"
          value={formatCompact(stats.totalStudioUses)}
          icon={Zap}
          accent="violet"
          hint={`${studioPerUser.toFixed(1)} avg / user`}
          trend={{ value: `${formatNumber(stats.totalTokensUsed)} tokens`, direction: 'up' }}
        />
        <KpiCard
          label="Recipes"
          value={stats.totalRecipes}
          icon={BookOpen}
          accent="sky"
          hint={`+${stats.recipesCreatedThisWeek} this week`}
          trend={{
            value: `+${stats.recipesCreatedToday} today`,
            direction: stats.recipesCreatedThisWeek > 0 ? 'up' : 'neutral',
          }}
        />
      </div>

      {/* ── Activity pulse + ChefCoin economy ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="lg:col-span-2 rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <Sparkles className="h-4 w-4 text-orange-500" />
              Activity Pulse
              <span className="ml-auto text-xs font-normal text-gray-400">last 14 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityPulse days={stats.activityTimeseries} />
            <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3 dark:border-gray-800 text-center">
              <div>
                <div className="flex items-center justify-center gap-1 text-sm font-semibold text-orange-600 dark:text-orange-400">
                  <FileText className="h-3.5 w-3.5" /> {formatNumber(stats.recipesCreatedThisWeek)}
                </div>
                <div className="text-[10px] text-gray-400">recipes (7d)</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  <ArrowUpRight className="h-3.5 w-3.5" /> {formatNumber(stats.totalChefcoinsEarned)}
                </div>
                <div className="text-[10px] text-gray-400">coins earned life.</div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1 text-sm font-semibold text-rose-600 dark:text-rose-400">
                  <ArrowDownRight className="h-3.5 w-3.5" /> {formatNumber(stats.totalChefcoinsSpent)}
                </div>
                <div className="text-[10px] text-gray-400">coins spent life.</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ChefCoin economy card */}
        <Card className="relative rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm overflow-hidden">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-amber-200/20 to-transparent dark:from-amber-500/10" />
          <CardHeader className="relative pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <ChefCoinIcon className="h-4 w-4 text-yellow-500" />
              ChefCoin Economy
            </CardTitle>
          </CardHeader>
          <CardContent className="relative space-y-3">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">Pool Allocated</div>
              <div className="flex items-baseline gap-1.5">
                <ChefCoinIcon className="h-6 w-6 text-yellow-500" />
                <span className="text-3xl font-semibold text-gray-900 dark:text-white">
                  {formatCompact(stats.totalChefcoinsAllocated)}
                </span>
                <span className="text-xs text-gray-400">held by {formatNumber(stats.totalUsers)} users</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/15 px-3 py-2">
                <div className="flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300">
                  <TrendingUp className="h-3 w-3" /> Today Earned
                </div>
                <div className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">+{formatNumber(stats.chefcoinsEarnedToday)}</div>
              </div>
              <div className="rounded-lg bg-rose-50 dark:bg-rose-900/15 px-3 py-2">
                <div className="flex items-center gap-1 text-[10px] text-rose-700 dark:text-rose-300">
                  <ArrowDownRight className="h-3 w-3" /> Today Spent
                </div>
                <div className="text-sm font-semibold text-rose-700 dark:text-rose-300">-{formatNumber(stats.chefcoinsSpentToday)}</div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-100 dark:border-gray-800 px-3 py-1.5 text-xs">
              <span className="inline-flex items-center gap-1 text-gray-500"><Coins className="h-3 w-3" /> Lifetime velocity</span>
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {formatNumber(stats.totalChefcoinsEarned + stats.totalChefcoinsSpent)} flow
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Composition cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CompositionCard
          title="User Roles"
          icon={Crown}
          iconClass="text-yellow-500"
          total={stats.totalUsers}
          segments={[
            { label: 'Admins', value: stats.adminCount, color: 'bg-amber-400', icon: Crown },
            { label: 'Readers', value: stats.readerCount, color: 'bg-sky-500', icon: Star },
            { label: 'Guests', value: stats.guestCount, color: 'bg-gray-400', icon: Users },
          ]}
        />
        <CompositionCard
          title="Subscriptions"
          icon={Gem}
          iconClass="text-purple-500"
          total={stats.totalUsers}
          segments={[
            { label: 'Premium', value: stats.premiumUsers, color: 'bg-purple-500', icon: Gem },
            { label: 'Standard', value: stats.standardUsers, color: 'bg-sky-500', icon: Star },
            { label: 'Free', value: stats.freeUsers, color: 'bg-gray-400', icon: Users },
            { label: 'Banned', value: stats.bannedUsers, color: 'bg-rose-500', icon: Activity },
          ]}
        />
        <CompositionCard
          title="Activity Tiers"
          icon={Activity}
          iconClass="text-emerald-500"
          total={stats.totalUsers}
          segments={[
            { label: 'Active now', value: stats.activeNowCount, color: 'bg-emerald-500', icon: Activity },
            { label: 'Today', value: stats.activeTodayCount, color: 'bg-emerald-400', icon: Clock },
            { label: 'This week', value: stats.activeThisWeekCount, color: 'bg-sky-500', icon: Clock },
            { label: 'This month', value: stats.activeThisMonthCount, color: 'bg-amber-500', icon: Clock },
            { label: 'Inactive', value: stats.inactiveCount, color: 'bg-gray-400', icon: Users },
          ]}
        />
      </div>

      {/* ── Leaderboards ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <Rocket className="h-4 w-4 text-orange-500" />
              Top Studio Power Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StudioPowerList stats={stats} />
          </CardContent>
        </Card>

        <Card className="rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <UserPlus className="h-4 w-4 text-sky-500" />
              Recent Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LeaderList entries={stats.recentSignups} emptyText="No users yet" />
          </CardContent>
        </Card>
      </div>

      {/* ── Secondary leaderboards: top spenders + top creators ────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <ChefCoinIcon className="h-4 w-4 text-yellow-500" />
              Top Coin Spenders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LeaderList entries={stats.topSpenders} emptyText="No ChefCoin activity yet" />
          </CardContent>
        </Card>

        <Card className="rounded-xl border border-gray-200/80 dark:border-gray-800 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300">
              <BookOpen className="h-4 w-4 text-sky-500" />
              Top Recipe Creators
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LeaderList entries={stats.topCreators} emptyText="No recipes created yet" />
          </CardContent>
        </Card>
      </div>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between text-[11px] text-gray-400">
        <span className="inline-flex items-center gap-1">
          <Activity className="h-3 w-3" /> Live data from get_admin_users_overview
        </span>
        <span>Last updated {stats.lastDataRefresh.toLocaleString()}</span>
      </div>
    </div>
  );
};

export default DashboardOverview;